'use client'

import * as React from 'react'
import { Transition } from '@headlessui/react'
import {
  CheckCircle,
  Info,
  AlertTriangle,
  XCircle,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'

export type ToastVariant = 'success' | 'info' | 'warning' | 'danger'

export interface ToastProps {
  show: boolean
  variant?: ToastVariant
  title: string
  description?: string
  onClose: () => void
}

const variantStyle: Record<
  ToastVariant,
  { icon: React.ComponentType<{ className?: string }>; iconColor: string; accent: string }
> = {
  success: { icon: CheckCircle, iconColor: 'text-[var(--color-green)]', accent: 'bg-[var(--color-green-muted)]' },
  info: { icon: Info, iconColor: 'text-[var(--color-cyan)]', accent: 'bg-[var(--color-cyan-muted)]' },
  warning: { icon: AlertTriangle, iconColor: 'text-[var(--color-orange)]', accent: 'bg-[var(--color-orange-muted)]' },
  danger: { icon: XCircle, iconColor: 'text-[var(--color-pink)]', accent: 'bg-[var(--color-pink-muted)]' },
}

export const Toast: React.FC<ToastProps> = ({
  show,
  variant = 'info',
  title,
  description,
  onClose,
}) => {
  const { icon: Icon, iconColor, accent } = variantStyle[variant]

  return (
    <Transition
      show={show}
      enter="transform ease-out duration-200 transition"
      enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
      enterTo="translate-y-0 opacity-100 sm:translate-x-0"
      leave="transition ease-in duration-150"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div
        role="status"
        className={cn(
          'pointer-events-auto w-80 max-w-full overflow-hidden rounded-xl border shadow-lg',
        )}
        style={{
          background: 'var(--color-surface)',
          borderColor: 'var(--color-border)',
        }}
      >
        <div className="flex items-start gap-3 p-3">
          <div className={cn('flex-shrink-0 rounded-lg p-1.5', accent)}>
            <Icon className={cn('h-4 w-4', iconColor)} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              {title}
            </p>
            {description && (
              <p className="mt-0.5 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Dismiss notification"
            className="flex-shrink-0 rounded p-1 transition-colors hover:bg-[var(--color-surface-hover)]"
            style={{ color: 'var(--color-text-muted)' }}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </Transition>
  )
}

Toast.displayName = 'Toast'
